/**
 * Client activity statement.
 *
 * Calculated from issued invoices and invoice_payments. Nothing here is stored.
 *
 * Dates:
 * - Invoice charge: invoices.invoice_date. If that date is empty, the UTC
 *   calendar date of invoices.sent_at. created_at is never used.
 * - Payment: invoice_payments.payment_date on the receipt row.
 * - Reversal: invoice_payments.payment_date on the reversal row (the date
 *   recorded by cp_reverse_invoice_payment).
 *
 * Draft and archived invoices are omitted. Reopening an issued invoice to
 * draft, or archiving it, removes it from later statements because CrewPilot
 * does not keep a separate issue-history table. That gap is reported; it is
 * not filled in.
 */

import { supabase } from "../supabase";
import { requireClientStatementView } from "./financialGuards";
import {
  applyOrganizationScope,
  resolveOrganizationIdForQueries,
} from "./orgQueryScope";
import {
  INVOICE_PAYMENTS_EMBED,
  isMissingInvoicePaymentsRelation,
} from "./invoices";
import { formatPaymentMethod } from "./accountsReceivable";
import { isReversalPayment, toCents } from "./invoiceMoney";

export const STATEMENT_ISSUED_STATUSES = Object.freeze(["sent", "paid"]);

export const STATEMENT_DATE_FIELDS = Object.freeze({
  invoiceCharge: "invoices.invoice_date, otherwise the UTC date of invoices.sent_at",
  payment: "invoice_payments.payment_date",
  reversal: "invoice_payments.payment_date on the reversal row",
});

const KIND_ORDER = Object.freeze({
  invoice: 0,
  payment: 1,
  reversal: 2,
});

const ISO_DATE = /^(\d{4}-\d{2}-\d{2})/;

export function isoDateOnly(value) {
  if (value == null || value === "") return "";
  const match = String(value).trim().match(ISO_DATE);
  return match ? match[1] : "";
}

export function formatStatementDate(isoDate) {
  const date = isoDateOnly(isoDate);
  if (!date) return "—";
  const [year, month, day] = date.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function defaultStatementPeriod(today = new Date()) {
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return {
    periodStart: `${year}-${month}-01`,
    periodEnd: `${year}-${month}-${day}`,
  };
}

export function statementChargeDate(invoice) {
  const invoiceDate = isoDateOnly(invoice?.invoice_date);
  if (invoiceDate) {
    return { date: invoiceDate, source: "invoice_date" };
  }
  const sentAt = isoDateOnly(invoice?.sent_at);
  if (sentAt) {
    return { date: sentAt, source: "sent_at" };
  }
  return null;
}

function paymentReference(payment) {
  const reference = String(payment?.payment_reference || "").trim();
  if (reference) return reference;
  const method = formatPaymentMethod(payment?.payment_method);
  return method === "—" ? "Payment" : method;
}

function compareEvents(left, right) {
  if (left.date !== right.date) return left.date < right.date ? -1 : 1;
  const kind = (KIND_ORDER[left.kind] ?? 9) - (KIND_ORDER[right.kind] ?? 9);
  if (kind !== 0) return kind;
  const reference = String(left.reference).localeCompare(String(right.reference));
  if (reference !== 0) return reference;
  return String(left.id).localeCompare(String(right.id));
}

function clientRecord(invoice) {
  return invoice?.clients || {};
}

export function statementClientLines(client = {}) {
  const lines = [];
  const contact = String(client.contact_name || "").trim();
  const address = String(client.address || "").trim();
  const locality = [client.city, client.province, client.postal_code]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
  const country = String(client.country || "").trim();
  const email = String(client.email || "").trim();
  const phone = String(client.phone || "").trim();
  if (contact) lines.push(contact);
  if (address) lines.push(address);
  if (locality) lines.push(locality);
  if (country) lines.push(country);
  if (email) lines.push(email);
  if (phone) lines.push(phone);
  return lines;
}

function projectName(invoice) {
  return String(invoice?.projects?.project_name || "").trim();
}

/**
 * @param {object} input
 * @param {string} input.clientId
 * @param {string} [input.organizationId]
 * @param {object[]} input.invoices
 * @param {string} input.periodStart YYYY-MM-DD
 * @param {string} input.periodEnd YYYY-MM-DD statement / as-of date
 */
export function buildClientStatement({
  clientId,
  organizationId = "",
  invoices = [],
  periodStart,
  periodEnd,
} = {}) {
  const start = isoDateOnly(periodStart);
  const end = isoDateOnly(periodEnd);
  if (!start || !end || start !== periodStart || end !== periodEnd) {
    throw new Error("Choose a statement period.");
  }
  if (start > end) {
    throw new Error("The statement end date must be on or after the start date.");
  }
  if (!clientId) {
    throw new Error("Choose a client.");
  }

  const limitations = [];
  const events = [];
  let client = null;

  for (const invoice of invoices || []) {
    if (invoice?.client_id !== clientId) continue;
    if (organizationId && invoice.organization_id !== organizationId) continue;

    const status = invoice.status;
    if (!STATEMENT_ISSUED_STATUSES.includes(status)) continue;

    if (!client) client = clientRecord(invoice);
    const charge = statementChargeDate(invoice);
    if (!charge) {
      limitations.push(
        `${invoice.invoice_number || "Invoice"} has no invoice date or issue date, so it was left off this statement.`,
      );
      continue;
    }

    const totalCents = toCents(invoice.total_amount);
    if (charge.date <= end) {
      events.push({
        id: `invoice:${invoice.invoice_id}`,
        kind: "invoice",
        date: charge.date,
        transaction: "Invoice",
        reference: invoice.invoice_number || "Invoice",
        projectName: projectName(invoice),
        chargeCents: totalCents,
        paymentCents: 0,
        invoiceId: invoice.invoice_id,
      });
    }

    const payments = Array.isArray(invoice.invoice_payments) ? invoice.invoice_payments : [];
    for (const payment of payments) {
      const date = isoDateOnly(payment?.payment_date);
      if (!date) {
        limitations.push(
          `A payment on ${invoice.invoice_number || "an invoice"} has no payment date, so it was left off this statement.`,
        );
        continue;
      }
      if (date > end) continue;
      const amount = toCents(payment.amount);
      if (isReversalPayment(payment)) {
        events.push({
          id: `reversal:${payment.payment_id}`,
          kind: "reversal",
          date,
          transaction: "Payment reversal",
          reference: paymentReference(payment),
          projectName: "",
          chargeCents: amount,
          paymentCents: 0,
          invoiceId: invoice.invoice_id,
        });
      } else {
        events.push({
          id: `payment:${payment.payment_id}`,
          kind: "payment",
          date,
          transaction: "Payment",
          reference: paymentReference(payment),
          projectName: "",
          chargeCents: 0,
          paymentCents: amount,
          invoiceId: invoice.invoice_id,
        });
      }
    }
  }

  events.sort(compareEvents);

  let running = 0;
  for (const event of events) {
    if (event.date < start) {
      running += event.chargeCents - event.paymentCents;
    }
  }
  const openingCents = running;

  const activity = [];
  let chargesCents = 0;
  let paymentsCents = 0;
  for (const event of events) {
    if (event.date < start || event.date > end) continue;
    running += event.chargeCents - event.paymentCents;
    chargesCents += event.chargeCents;
    paymentsCents += event.paymentCents;
    activity.push({ ...event, balanceCents: running });
  }

  const openItems = buildOpenItems(invoices, {
    clientId,
    organizationId,
    asOf: end,
  });
  const overdueCents = openItems
    .filter((item) => item.overdue)
    .reduce((sum, item) => sum + item.balanceCents, 0);

  return {
    clientId,
    organizationId: organizationId || "",
    clientName: String(client?.client_name || "").trim() || "Client",
    clientLines: statementClientLines(client || {}),
    periodStart: start,
    periodEnd: end,
    statementDate: end,
    openingCents,
    chargesCents,
    paymentsCents,
    closingCents: running,
    overdueCents,
    activity,
    openItems,
    limitations,
    calculationNote:
      "Draft and archived invoices are omitted. Reopening an issued invoice to draft removes it, because there is no separate issue history. Balances use invoice and payment dates, not today's invoice status.",
  };
}

function outstandingAsOf(invoice, asOf) {
  const charge = statementChargeDate(invoice);
  if (!charge || charge.date > asOf) return null;
  const payments = Array.isArray(invoice.invoice_payments) ? invoice.invoice_payments : [];
  const reversedIds = new Set();
  for (const payment of payments) {
    const date = isoDateOnly(payment?.payment_date);
    if (isReversalPayment(payment) && date && date <= asOf && payment.reversal_of_payment_id) {
      reversedIds.add(payment.reversal_of_payment_id);
    }
  }
  let paid = 0;
  for (const payment of payments) {
    const date = isoDateOnly(payment?.payment_date);
    if (!date || date > asOf || isReversalPayment(payment)) continue;
    if (reversedIds.has(payment.payment_id)) continue;
    paid += toCents(payment.amount);
  }
  return Math.max(toCents(invoice.total_amount) - paid, 0);
}

function buildOpenItems(invoices, { clientId, organizationId, asOf }) {
  const items = [];
  for (const invoice of invoices || []) {
    if (invoice?.client_id !== clientId) continue;
    if (organizationId && invoice.organization_id !== organizationId) continue;
    if (!STATEMENT_ISSUED_STATUSES.includes(invoice.status)) continue;
    const balance = outstandingAsOf(invoice, asOf);
    if (balance == null || balance <= 0) continue;
    const dueDate = isoDateOnly(invoice.due_date);
    const charge = statementChargeDate(invoice);
    items.push({
      invoiceId: invoice.invoice_id,
      invoiceNumber: invoice.invoice_number || "Invoice",
      projectName: projectName(invoice),
      invoiceDate: charge?.date || "",
      dueDate,
      totalCents: toCents(invoice.total_amount),
      balanceCents: balance,
      overdue: Boolean(dueDate) && dueDate < asOf,
    });
  }
  items.sort((left, right) => {
    const due = String(left.dueDate).localeCompare(String(right.dueDate));
    if (due !== 0) return due;
    return String(left.invoiceNumber).localeCompare(String(right.invoiceNumber));
  });
  return items;
}

const STATEMENT_INVOICE_SELECT = `
  invoice_id,
  organization_id,
  client_id,
  project_id,
  invoice_number,
  title,
  status,
  total_amount,
  invoice_date,
  sent_at,
  due_date,
  clients(client_name, contact_name, phone, email, address, city, province, postal_code, country),
  projects(project_name),
  ${INVOICE_PAYMENTS_EMBED}
`;

export async function loadClientStatementInvoices(actor, client = supabase) {
  requireClientStatementView(actor);
  const organizationId = await resolveOrganizationIdForQueries(actor, client);

  let query = client
    .from("invoices")
    .select(STATEMENT_INVOICE_SELECT)
    .in("status", [...STATEMENT_ISSUED_STATUSES])
    .order("invoice_date", { ascending: true, nullsFirst: false });

  query = applyOrganizationScope(query, organizationId, { tableName: "invoices" });
  const { data, error } = await query;

  if (isMissingInvoicePaymentsRelation(error)) {
    throw new Error(
      "Client statements need the invoice payment ledger. It is not available on this database.",
    );
  }
  if (error) throw error;

  return {
    organizationId: organizationId || "",
    invoices: data || [],
  };
}
